import { NOTE_MOST, isKeptNote } from "../document/note.ts";
import { isId, isIntegerIn, isOpId, recordOf } from "./op-read.ts";
import { OP_FRAME_MAX, OP_MAP_SIDE_MAX, type LiveOp } from "./op-types.ts";
import { opOf } from "./ops.ts";
import { hasExactly, isEditorId, isEditorName, isMoment, type Fields } from "./protocol-shared.ts";

/**
 * The stream's messages (ADR-086 decisions 21, 25, 28, 29, 30 and 33): what a tab and its build server say on
 * the live channel beside presence once the server runs the shared operation stream. From a tab: `resume`
 * (what it holds), `op` (an operation, flat beside its `t`), `claim` and `release` (a soft lock on a layer or
 * on a rectangle of tiles) and `save` (name this moment a version). From the server: `stream` (the room as it
 * stands), `op` (an operation with its sequence and the editor it came from), `ack` and `nack` (the answer to
 * the sender's own operation), `resync` (read a snapshot), `claims` (every soft lock held), `version`,
 * `checkpoint`, `library` and `frozen`. Each reader is exact as presence's are: a message is accepted only with
 * exactly its fields, each of its type and within its bounds.
 */

/**
 * The most code units a stream message adds around the operation it carries: its `t`, and from the server
 * the sequence and the editor's id.
 */
export const LIVE_OP_WRAP_MAX = 96;

/**
 * The longest frame that carries an operation, in UTF-16 code units: the operation's own limit and its
 * wrapper. At three bytes a unit it stays inside a socket frame of 256 KiB.
 */
export const LIVE_OP_FRAME_MAX = OP_FRAME_MAX + LIVE_OP_WRAP_MAX;

/**
 * The longest frame the server sends that carries no operation, in UTF-16 code units.
 */
export const LIVE_SERVER_FRAME_MAX = 65_536;

/**
 * The longest note a named version carries, in UTF-16 code units: the one note rule's length
 * (`document/note.ts`), which a published version's note keeps too.
 */
export const LIVE_NOTE_MAX = NOTE_MOST;

/**
 * The most claims one editor holds.
 */
export const CLAIMS_PER_EDITOR = 4;

/**
 * The most claims a world's room holds.
 */
export const CLAIMS_PER_WORLD = 32;

/**
 * The largest protocol version a message names.
 */
export const LIVE_VERSION_MAX = 0xffff;

/**
 * Why the server refuses an operation it did not weigh: the sender's frames pass the `rate` limit and the
 * operation may be sent again later; the sender's account is `denied` saving; the room is `frozen`; the socket
 * never joined the stream of this `version`.
 */
export const NACK_REASONS = ["rate", "denied", "frozen", "version"] as const;

/**
 * One reason of `NACK_REASONS`.
 */
export type NackReason = (typeof NACK_REASONS)[number];

/**
 * Why a room stops taking operations until its tabs reload: its `journal` cannot be written, the document it
 * would flush is `invalid` (it does not read back), or the head was `replaced` behind it.
 */
export const FROZEN_REASONS = ["journal", "invalid", "replaced"] as const;

/**
 * One reason of `FROZEN_REASONS`.
 */
export type FrozenReason = (typeof FROZEN_REASONS)[number];

/**
 * The kinds of library document this reader knows, each read by its own shape. A notice of another kind is
 * read with `kind` a string of 1 to `LIBRARY_KIND_MAX` code units and `frame` a frame id or null, so a tab
 * skips a kind a later build adds and keeps its socket. `studio` (ADR-107 decision 35) is a studio pack's document,
 * its id the pack: its notice names the one frame a save changed, or none for a save that changed more, which is
 * the shape a reader from before the kind already took for a kind it did not know.
 */
export const LIBRARY_KINDS = ["footprint", "template", "preset", "brushset", "studio"] as const;

/**
 * The longest `kind` a `library` notice may carry, in UTF-16 code units.
 */
export const LIBRARY_KIND_MAX = 32;

/**
 * One kind of `LIBRARY_KINDS`.
 */
export type LibraryKind = (typeof LIBRARY_KINDS)[number];

/**
 * What a claim covers: a layer of a map, or a rectangle of a map in tiles.
 */
export type ClaimTarget = { readonly map: string; readonly layer: string } | { readonly map: string; readonly x: number; readonly y: number; readonly w: number; readonly h: number };

/**
 * A claim as the room tells it: the editor that holds it, by the id the server gave its socket and the name
 * the host shows, and what it covers.
 */
export type LiveClaim = { readonly by: string; readonly name: string } & ClaimTarget;

/**
 * What a tab holds of the stream: the protocol version it speaks, and the epoch and sequence of the document
 * it has.
 */
export interface ResumePoint {
  readonly v: number;
  readonly epoch: string;
  readonly seq: number;
}

/**
 * What a tab sends on the stream.
 */
export type StreamClientMessage = ({ readonly t: "resume" } & ResumePoint) | ({ readonly t: "op" } & LiveOp) | ({ readonly t: "claim" } & ClaimTarget) | ({ readonly t: "release" } & ClaimTarget) | { readonly t: "save"; readonly note: string | null };

/**
 * What the server sends on the stream. A `library` notice's `kind` is one of `LIBRARY_KINDS` or a kind a later
 * build added, which a tab skips.
 */
export type StreamServerMessage =
  | { readonly t: "stream"; readonly v: number; readonly epoch: string; readonly seq: number; readonly floor: number; readonly revision: number }
  | ({ readonly t: "op"; readonly seq: number; readonly by: string } & LiveOp)
  | { readonly t: "ack"; readonly id: string; readonly seq: number | null }
  | { readonly t: "nack"; readonly id: string; readonly reason: NackReason }
  | { readonly t: "resync" }
  | { readonly t: "claims"; readonly list: readonly LiveClaim[] }
  | { readonly t: "version"; readonly revision: number; readonly at: string; readonly by: string; readonly name: string | null; readonly note: string | null }
  | { readonly t: "unchanged" }
  | { readonly t: "unsaved" }
  | { readonly t: "checkpoint"; readonly seq: number; readonly sha256: string }
  | { readonly t: "library"; readonly kind: string; readonly id: string; readonly frame: string | null; readonly by?: string | null }
  | { readonly t: "frozen"; readonly reason: FrozenReason };

const EPOCH = /^[A-Za-z0-9_-]{1,32}$/;
const SHA256 = /^[0-9a-f]{64}$/;
const LIBRARY_ID = /^[a-z0-9][a-z0-9._-]{0,127}$/;
const FRAME_ID = /^[^/]+\/[^/]+\/(0|[1-9][0-9]*)$/;
const FRAME_ID_MAX = 256;

/**
 * Whether a value is a room's epoch: the id of one opening of a room.
 *
 * @param value The value.
 * @returns True for 1 to 32 letters, digits, dashes and underscores.
 */
export function isEpoch(value: unknown): value is string {
  return typeof value === "string" && EPOCH.test(value);
}

/**
 * Whether a value is a sequence or a revision: a whole number a double holds exactly.
 *
 * @param value The value.
 * @param least The least allowed.
 * @returns True for a safe integer from `least`.
 */
export function isCount(value: unknown, least: number): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= least;
}

/**
 * Whether a value is a version's note as it is kept: trimmed, not blank, and within the one note rule
 * (`document/note.ts`), which the publish route and the versions record judge a note by too.
 *
 * @param value The value.
 * @returns True for such a string.
 */
export function isNote(value: unknown): value is string {
  return isKeptNote(value);
}

/**
 * Reads what a claim covers from the fields beside a message's or a claim's own.
 *
 * @param fields The fields.
 * @param own The other keys the fields must hold.
 * @returns The target rebuilt with exactly its fields, or null when the fields are not exactly a layer of a
 *   map or a rectangle of at least one tile inside the largest map an operation carries.
 */
function targetOf(fields: Fields, own: readonly string[]): ClaimTarget | null {
  const { map, layer, x, y, w, h } = fields;
  if (!isId(map)) {
    return null;
  }
  if (Object.hasOwn(fields, "layer")) {
    return hasExactly(fields, [...own, "map", "layer"]) && isId(layer) ? { map, layer } : null;
  }
  const inside = isIntegerIn(x, 0, OP_MAP_SIDE_MAX) && isIntegerIn(y, 0, OP_MAP_SIDE_MAX) && isIntegerIn(w, 1, OP_MAP_SIDE_MAX) && isIntegerIn(h, 1, OP_MAP_SIDE_MAX);
  return hasExactly(fields, [...own, "map", "x", "y", "w", "h"]) && inside ? { map, x, y, w, h } : null;
}

/**
 * Reads the operation a frame carries flat beside its own fields.
 *
 * @param fields The frame's fields.
 * @param own The frame's own keys, which the operation does not hold.
 * @returns The operation, or null when what is left is not exactly one.
 */
function carriedOp(fields: Fields, own: readonly string[]): LiveOp | null {
  return opOf(Object.fromEntries(Object.entries(fields).filter(([key]) => !own.includes(key))));
}

/**
 * Reads a tab's stream message from a frame's fields.
 *
 * @param fields The fields of a frame that named none of presence's types.
 * @returns The message, or null when the fields are not exactly one.
 */
export function streamClientOf(fields: Fields): StreamClientMessage | null {
  switch (fields["t"]) {
    case "resume": {
      const { v, epoch, seq } = fields;
      return hasExactly(fields, ["t", "v", "epoch", "seq"]) && isIntegerIn(v, 1, LIVE_VERSION_MAX) && isEpoch(epoch) && isCount(seq, 0) ? { t: "resume", v, epoch, seq } : null;
    }
    case "op": {
      const op = carriedOp(fields, ["t"]);
      return op === null ? null : { t: "op", ...op };
    }
    case "claim": {
      const target = targetOf(fields, ["t"]);
      return target === null ? null : { t: "claim", ...target };
    }
    case "release": {
      const target = targetOf(fields, ["t"]);
      return target === null ? null : { t: "release", ...target };
    }
    case "save": {
      const note = fields["note"];
      return hasExactly(fields, ["t", "note"]) && (note === null || isNote(note)) ? { t: "save", note } : null;
    }
    default:
      return null;
  }
}

/**
 * Reads one claim of a `claims` message.
 *
 * @param value The value.
 * @returns The claim, or null when it is not exactly one.
 */
function claimOf(value: unknown): LiveClaim | null {
  const fields = recordOf(value);
  if (fields === undefined || !isEditorId(fields["by"]) || !isEditorName(fields["name"])) {
    return null;
  }
  const target = targetOf(fields, ["by", "name"]);
  return target === null ? null : { by: fields["by"], name: fields["name"], ...target };
}

/**
 * Whether a value is a frame id a notice may carry.
 *
 * @param value The value.
 * @returns Whether it is one.
 */
function isFrameId(value: unknown): value is string {
  return typeof value === "string" && value.length <= FRAME_ID_MAX && FRAME_ID.test(value);
}

/**
 * Reads a `library` notice's fields.
 *
 * @param fields The fields.
 * @returns The message, or null when they are not exactly one: a footprint names the frame that changed, a
 *   template or a preset none, a brush set none and, optionally, `by`, the family name of the builder who deleted
 *   it or null (ADR-123 decision 8.4), a studio pack a frame or none, and a kind outside `LIBRARY_KINDS` (1 to `LIBRARY_KIND_MAX` code units) a
 *   frame or none, read as it came for the tab to skip.
 */
function libraryOf(fields: Fields): StreamServerMessage | null {
  const { kind, id, frame } = fields;
  if (kind === "brushset" && Object.hasOwn(fields, "by")) {
    const { by } = fields;
    const named = hasExactly(fields, ["t", "kind", "id", "frame", "by"]) && typeof id === "string" && LIBRARY_ID.test(id) && frame === null && (by === null || isEditorName(by));
    return named ? { t: "library", kind, id, frame, by: by as string | null } : null;
  }
  if (!hasExactly(fields, ["t", "kind", "id", "frame"]) || typeof id !== "string" || !LIBRARY_ID.test(id) || typeof kind !== "string") {
    return null;
  }
  if (kind === "footprint") {
    return isFrameId(frame) ? { t: "library", kind, id, frame } : null;
  }
  if (kind === "template" || kind === "preset" || kind === "brushset") {
    return frame === null ? { t: "library", kind, id, frame } : null;
  }
  return kind.length >= 1 && kind.length <= LIBRARY_KIND_MAX && (frame === null || isFrameId(frame)) ? { t: "library", kind, id, frame } : null;
}

/**
 * Reads the server's stream message from a frame's fields.
 *
 * @param fields The fields of a frame that named none of presence's types.
 * @returns The message, or null when the fields are not exactly one.
 */
export function streamServerOf(fields: Fields): StreamServerMessage | null {
  switch (fields["t"]) {
    case "stream": {
      const { v, epoch, seq, floor, revision } = fields;
      const valid = hasExactly(fields, ["t", "v", "epoch", "seq", "floor", "revision"]) && isIntegerIn(v, 1, LIVE_VERSION_MAX) && isEpoch(epoch) && isCount(seq, 0) && isCount(floor, 0) && floor <= seq && isCount(revision, 1);
      return valid ? { t: "stream", v, epoch, seq, floor, revision } : null;
    }
    case "op": {
      const { seq, by } = fields;
      const op = isCount(seq, 1) && isEditorId(by) ? carriedOp(fields, ["t", "seq", "by"]) : null;
      return op === null ? null : { t: "op", seq: seq as number, by: by as string, ...op };
    }
    case "ack": {
      const { id, seq } = fields;
      return hasExactly(fields, ["t", "id", "seq"]) && isOpId(id) && (seq === null || isCount(seq, 1)) ? { t: "ack", id, seq } : null;
    }
    case "nack": {
      const { id, reason } = fields;
      const known = NACK_REASONS.find((candidate) => candidate === reason);
      return hasExactly(fields, ["t", "id", "reason"]) && isOpId(id) && known !== undefined ? { t: "nack", id, reason: known } : null;
    }
    case "resync":
      return hasExactly(fields, ["t"]) ? { t: "resync" } : null;
    case "claims": {
      const list = fields["list"];
      if (!hasExactly(fields, ["t", "list"]) || !Array.isArray(list) || list.length > CLAIMS_PER_WORLD) {
        return null;
      }
      const claims = (list as readonly unknown[]).map(claimOf);
      return claims.every((claim) => claim !== null) ? { t: "claims", list: claims as LiveClaim[] } : null;
    }
    case "version": {
      const { revision, at, by, name, note } = fields;
      const valid = hasExactly(fields, ["t", "revision", "at", "by", "name", "note"]) && isCount(revision, 1) && isMoment(at) && isEditorId(by) && (name === null || isEditorName(name)) && (note === null || isNote(note));
      return valid ? { t: "version", revision: revision as number, at: at as string, by: by as string, name: name as string | null, note: note as string | null } : null;
    }
    case "unchanged":
      return hasExactly(fields, ["t"]) ? { t: "unchanged" } : null;
    case "unsaved":
      return hasExactly(fields, ["t"]) ? { t: "unsaved" } : null;
    case "checkpoint": {
      const { seq, sha256 } = fields;
      return hasExactly(fields, ["t", "seq", "sha256"]) && isCount(seq, 0) && typeof sha256 === "string" && SHA256.test(sha256) ? { t: "checkpoint", seq, sha256 } : null;
    }
    case "library":
      return libraryOf(fields);
    case "frozen": {
      const known = FROZEN_REASONS.find((candidate) => candidate === fields["reason"]);
      return hasExactly(fields, ["t", "reason"]) && known !== undefined ? { t: "frozen", reason: known } : null;
    }
    default:
      return null;
  }
}
